import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check, Lock, ChevronRight, FileText, Globe } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import EmergencyButton from '../components/EmergencyButton';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';

// Total number of supported translation languages — kept in sync with
// LessonViewPage's LANGUAGES map (en, ru, uk, pt, fr, it, es, ko = 8).
const TOTAL_LANGS = 8;
const TRACKED_LANG_CODES = ['en', 'ru', 'uk', 'pt', 'fr', 'it', 'es', 'ko'];

/**
 * Compact translation-coverage pill, visible to SuperAdmin only.
 * Counts how many of the eight tracked languages actually have non-empty
 * `content_<lang>` text on the lesson doc. We fall back to the stored
 * `available_languages` array if the API strips the per-language fields
 * from the list payload, but prefer the direct count so a stale metadata
 * array (empty on legacy rows) never renders as a misleading "1/8".
 */
function CoverageBadge({ lesson }) {
  const populated = TRACKED_LANG_CODES.filter(
    (l) => typeof lesson[`content_${l}`] === 'string' && lesson[`content_${l}`].trim().length > 0
  );
  const fromArray = Array.isArray(lesson.available_languages)
    ? lesson.available_languages.filter((l) => TRACKED_LANG_CODES.includes(l))
    : [];
  // Pick whichever source gives us the higher real count. If content_<lang>
  // was stripped from the API payload we fall back to available_languages.
  const count = Math.max(populated.length, fromArray.length);
  const isPlaceholder =
    !lesson.content_en || /being prepared/i.test(lesson.content_en || '');

  let label;
  let cls;
  if (isPlaceholder) {
    label = 'placeholder';
    cls = 'bg-zinc-100 text-zinc-500 border-zinc-200';
  } else if (count >= TOTAL_LANGS) {
    label = `${TOTAL_LANGS}/${TOTAL_LANGS} languages`;
    cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  } else if (count >= 4) {
    label = `${count}/${TOTAL_LANGS} languages`;
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
  } else {
    label = `${count || 1}/${TOTAL_LANGS} languages`;
    cls = 'bg-rose-50 text-rose-700 border-rose-200';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}
      data-testid={`coverage-${lesson.lesson_id}`}
    >
      <Globe className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

function LessonsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Globe/coverage badge is a translation-maintenance tool meant for
  // SuperAdmins only.  Beta testers see unrestricted content but should
  // NOT see the badge in the user view.
  const isSuperAdmin = user?.role === 'superadmin';
  const isBetaTester = user?.role === 'beta_tester';
  const isUnrestricted = isSuperAdmin || isBetaTester;
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/lessons`);
      if (response.ok) {
        setLessons(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const intro = lessons.find(l => l.lesson_id === 'intro');
  const weeklyLessons = lessons.filter(l => l.lesson_id !== 'intro');

  const handleLessonClick = (lesson) => {
    if (lesson.locked && !isUnrestricted) {
      toast.info('This lesson will unlock when you reach this week in the programme.');
      return;
    }
    navigate(`/lessons/${lesson.lesson_id}`);
  };

  return (
    <div className="min-h-screen bg-zinc-100 pb-24">
      <PageHeader title="Lessons" />

      <main className="pt-20 px-4">
        <div className="max-w-2xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Introduction Lesson - Always visible */}
              {intro && (
                <div className="mb-6">
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3">2-Week Window</p>
                  <button
                    onClick={() => navigate(`/lessons/${intro.lesson_id}`)}
                    className="w-full bg-white border-2 border-amber-300 rounded-xl p-5 text-left hover:border-amber-400 transition-colors"
                    data-testid="lesson-intro"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          intro.completed ? 'bg-green-100' : 'bg-amber-100'
                        }`}>
                          {intro.completed ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <BookOpen className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-zinc-900">Lesson Introduction</h3>
                          <p className="text-xs text-zinc-500 mt-0.5">{intro.subtitle}</p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className="text-xs text-zinc-400">{intro.estimated_time} min read</span>
                            {Object.keys(intro.pdf_urls || {}).length > 0 && (
                              <span className="text-xs text-blue-600">{Object.keys(intro.pdf_urls).length} languages</span>
                            )}
                            {isSuperAdmin && <CoverageBadge lesson={intro} />}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                    </div>
                  </button>
                </div>
              )}

              {/* Weekly Lessons */}
              <div className="mb-6">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">Programme Weeks</p>
                <div className="space-y-2">
                  {weeklyLessons.map(lesson => {
                    const isLocked = isUnrestricted ? false : lesson.locked;
                    return (
                      <button
                        key={lesson.lesson_id}
                        onClick={() => handleLessonClick(lesson)}
                        className={`w-full bg-white border rounded-lg p-4 text-left transition-colors ${
                          isLocked ? 'border-zinc-100 opacity-50' : 'border-zinc-200 hover:border-zinc-400'
                        }`}
                        data-testid={`lesson-${lesson.lesson_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              lesson.completed ? 'bg-green-100' : isLocked ? 'bg-zinc-50' : 'bg-blue-50'
                            }`}>
                              {lesson.completed ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : isLocked ? (
                                <Lock className="w-3 h-3 text-zinc-300" />
                              ) : (
                                <span className="text-xs font-bold text-blue-600">{lesson.order || lesson.week}</span>
                              )}
                            </div>
                            <div>
                              {(() => {
                                const n = lesson.order ?? lesson.week ?? 0;
                                const title = (lesson.title || '').trim();
                                if (n === 0) {
                                  return (
                                    <h3 className={`text-sm font-semibold ${isLocked ? 'text-zinc-400' : 'text-zinc-900'}`}>
                                      Introduction
                                    </h3>
                                  );
                                }
                                return (
                                  <>
                                    <h3 className={`text-sm font-semibold ${isLocked ? 'text-zinc-400' : 'text-zinc-900'}`}>
                                      Lesson {n}: {title || `Lesson ${n}`}
                                    </h3>
                                  </>
                                );
                              })()}
                              {isSuperAdmin && (
                                <div className="mt-1.5">
                                  <CoverageBadge lesson={lesson} />
                                </div>
                              )}
                            </div>
                          </div>
                          {isLocked ? (
                            <Lock className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default LessonsPage;
